import { authGuard } from './routes/auth.guard';
import { unsavedChangesGuard } from './routes/unsaved-changes.guard';
import { LoginRedirectComponent } from '@app/shared/auth/login/login-redirect.component';
import { SignUpRedirectComponent } from '@app/shared/auth/sign-up/sign-up-redirect.component';
import { LetsGetYouConnectedComponent } from '@app/shared/auth/lets-get-you-connected/lets-get-you-connected.component';
import { AuthCallbackComponent } from '@app/shared/auth/auth-callback/auth-callback.component';
import { RootLoadingComponent } from '@app/shared/root-loading/root-loading.component';
import { SignInSsoComponent } from '@app/modules/sign-in-sso/sign-in-sso.component';
import { Routes, UrlSegment, UrlMatchResult } from '@angular/router';
import { CareerSurveyComponent } from './modules/career-survey/career-survey.component';
import { SignUpComponent } from './modules/sign-up/sign-up.component';
import { CareerResultsComponent } from './modules/career-results/career-results.component';
import { CareerDetailsComponent } from './modules/career-details/career-details.component';
import { UserBiqComponent } from './modules/user-biq/user-biq.component';
import { BuildPlanComponent } from './modules/build-plan/build-plan.component';
import { FindJobComponent } from './modules/find-job/find-job.component';
import { TermsConditionsComponent } from './modules/legal/terms-conditions/terms-conditions.component';
import { environment } from '@environments/environment';
import { MyProfileComponent } from './shared/biq/my-profile/my-profile.component';
import { AccessibilityComponent } from './modules/accessibility/accessibility.component';
import { FindJobModuleComponent } from './modules/find-job/find-job-module/find-job-module.component';
import { TermsAndConditionsComponent } from './modules/terms-conditions/terms-conditions.component';
import { RedirectUserGuard } from './routes/redirect-user.guard';
import { routeToTenantId } from '@app/config/auth-constants';
import { NotFoundComponent } from './modules/not-found/not-found.component';
import { EndPageComponent } from './shared/biq/end-page/end-page.component';

// Custom URL matcher for SSO tenant routes
export function ssoTenantMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length === 1) {
    const path = segments[0].path;
    if (routeToTenantId[path]) {
      return { consumed: segments };
    }
  }
  return null;
}

export const routes: Routes = [
  {
    path: "",
    pathMatch: "full",
    component: RootLoadingComponent,
  },
  {
    path: "callbacks/auth",
    component: AuthCallbackComponent,
  },
  {
    path: "user-info",
    component: UserBiqComponent,
    canActivate: [authGuard, RedirectUserGuard],
    title: "Background Information - Compass",
  },
  {
    path: "user-info/:step",
    component: UserBiqComponent,
    canActivate: [authGuard, RedirectUserGuard],
    title: "Background Information - Compass",
  },
  {
    path: "user-info-set",
    component: EndPageComponent,
    canActivate: [authGuard],
    title: "Background Information - Compass",
  },
  {
    path: "survey",
    component: CareerSurveyComponent,
    canActivate: [authGuard, RedirectUserGuard],
    title: "Career Survey Introduction - Compass",
  },
  {
    path: "survey/:step",
    component: CareerSurveyComponent,
    canActivate: [authGuard, RedirectUserGuard],
    title: "Career Survey - Compass",
  },
  {
    path: "career-matches",
    component: CareerResultsComponent,
    canActivate: [authGuard, RedirectUserGuard],
    title: "Career Matches - Compass",
  },
  {
    path: "login",
    component: LoginRedirectComponent,
    title: "Login",
  },
  {
    matcher: ssoTenantMatcher,
    component: SignInSsoComponent,
    title: "SSO Sign In - Compass",
  },
  {
    path: "signup",
    component: SignUpRedirectComponent,
    title: "Sign Up - Compass",
  },
  {
    path: "lets-get-you-connected",
    component: LetsGetYouConnectedComponent,
  },
  {
    path: "career-details/:onetCode",
    component: CareerDetailsComponent,
    canActivate: [authGuard, RedirectUserGuard],
    title: "Career Details - Compass",
  },
  {
    path: "build-plan",
    component: BuildPlanComponent,
    canActivate: [authGuard, RedirectUserGuard],
    title: "Build Your Plan - Compass",
  },
  {
    path: "find-job",
    component: FindJobModuleComponent,
    canActivate: [authGuard, RedirectUserGuard],
    title: "Find a Job - Compass",
  },
  {
    path: "legal/terms",
    component: TermsConditionsComponent,
    title: "Legal Terms - Compass",
  },
  {
    path: "profile",
    component: MyProfileComponent,
    canActivate: [authGuard],
    canDeactivate: [unsavedChangesGuard],
    title: "My Profile - Compass",
  },
  {
    path: "accessibility",
    component: AccessibilityComponent,
    title: "Accessibility - Compass",
  },
  {
    path: "terms",
    component: TermsAndConditionsComponent,
    canActivate: [authGuard, RedirectUserGuard],
    title: "Terms and Conditions - Compass",
  },
  {
    path: "**",
    component: NotFoundComponent,
    title: "Page Not Found - Compass",
  },
];

// NOTE: This is here so we have an unauthenticated route
// for setting our JWT cookie in local-dev environment only.
// The fact it's the SignUpComponent is irrelevant.
if (environment.appEnv === 'local-dev') {
  routes.push({
    path: "local",
    component: SignUpComponent,
  });
}
